import { getContext, setContext } from "svelte";
import copy from "copy-to-clipboard";
import {
    checkStripeAccountSE,
    createStripeAccountLinkSE,
    createStripeAccountSE,
    createStripeDashboardPortalSE,
    getReferralMetricsCachedSE,
    updateReferralCodeSE,
} from "@/services/referrals";
import { updateDocById } from "@/services/firebase";
import appStore from "@/utils/store";
import { goTo } from "@/utils/router";
import { translate } from "@/utils/format";
import { LANG } from "../lib/lang";
import {
    readSectionFromUrl,
    stripUrlParams,
    writeSectionToUrl,
    type SectionId,
} from "../lib/sections";
import { downloadQr } from "../lib/qr";
import * as fmt from "../lib/format";
import {
    METRICS_CACHE_MAX_AGE_MS,
    asNumber,
    buildMetricsQuery,
    daysForRange,
    isCacheFresh,
    isRecord,
    parseMetrics,
    readMetricsCache,
    writeMetricsCache,
} from "../lib/metrics";
import type {
    ActivityItem,
    DailyPoint,
    MetricsCacheEntry,
    RangeKey,
    SubscriptionRow,
    UserRow,
} from "../lib/types";

const SNACKBAR_MS = 5000;

/**
 * Shared state of the partner dashboard. Created once per mounted dashboard
 * (not a module singleton, so nothing leaks between logout/login) and handed
 * to sections through context.
 *
 * Methods are arrow properties so they can be passed straight to `onclick`.
 */
export class DashboardStore {
    // ── identity / referral code ──
    user = $state<any>(null);
    referralCode = $state("");
    oldReferralCode = $state("");
    editingCode = $state(false);
    referralCodeTrimmed = $derived((this.referralCode || "").trim());
    referralLink = $derived(
        this.referralCodeTrimmed
            ? `https://link.caloo.app/r/${this.referralCodeTrimmed}`
            : "",
    );

    // ── stripe connect ──
    stripeAccountId = $state<string | null>(null);
    stripeReady = $state(false);
    isStripeConfigured = $derived(!!this.stripeAccountId && this.stripeReady);

    // ── ui ──
    loading = $state(false);
    activeSnackBar = $state(false);
    snackBarText = $state("");
    section = $state<SectionId>("overview");
    range = $state<RangeKey>("30d");
    #snackTimer: ReturnType<typeof setTimeout> | null = null;

    // ── metrics ──
    installs = $state(0);
    signups = $state(0);
    subscribers = $state(0);
    series = $state<DailyPoint[]>([]);
    activity = $state<ActivityItem[]>([]);
    subscriptionsList = $state<SubscriptionRow[]>([]);
    usersList = $state<UserRow[]>([]);
    metricsHasSeries = $state(false);
    metricsHasActivity = $state(false);
    metricsHasSubscriptions = $state(false);
    metricsHasUsers = $state(false);
    lastMetricsUpdatedAt = $state<number | null>(null);
    #warmingMetricsCache = false;

    // ── derived ──
    uiLocale = $derived(DashboardStore.#getUiLocale());
    rangeDaysLabel = $derived(
        this.range === "all"
            ? translate("PARTNER_PERIOD_ALL", "Historial completo")
            : `${daysForRange(this.range)} ${translate("TIME_DAYS", "días")}`,
    );
    installsRange = $derived(this.series.reduce((a, p) => a + p.installs, 0));
    newSubsRange = $derived(this.series.reduce((a, p) => a + p.newSubs, 0));
    cancelsRange = $derived(this.series.reduce((a, p) => a + p.cancels, 0));
    renewalsRange = $derived(this.series.reduce((a, p) => a + p.renewals, 0));
    conversion = $derived(
        this.installs > 0 ? this.subscribers / this.installs : 0,
    );
    conversionRange = $derived(
        this.installsRange > 0 ? this.newSubsRange / this.installsRange : 0,
    );
    installsChart = $derived({
        labels: this.series.map((p) => this.formatShortDay(p.date)),
        values: this.series.map((p) => p.installs),
        units: "",
    });
    subsChart = $derived({
        labels: this.series.map((p) => this.formatShortDay(p.date)),
        values: this.series.map((p) => p.newSubs),
        units: "",
    });
    subsActive = $derived(
        this.subscriptionsList.filter((s) => s.status === "active").length,
    );
    subsTrial = $derived(
        this.subscriptionsList.filter((s) => s.status === "trialing").length,
    );
    subsPastDue = $derived(
        this.subscriptionsList.filter((s) => s.status === "past_due").length,
    );
    subsCanceled = $derived(
        this.subscriptionsList.filter((s) => s.status === "canceled").length,
    );
    renewSoon = $derived(
        this.subscriptionsList
            .filter((s) => s.status === "active" || s.status === "trialing")
            .slice(0, 6),
    );
    usersInstalled = $derived(
        this.usersList.filter((u) => u.status === "installed").length,
    );
    usersRegistered = $derived(
        this.usersList.filter((u) => u.status === "registered").length,
    );
    usersSubscribed = $derived(
        this.usersList.filter((u) => u.status === "subscribed").length,
    );

    static #getUiLocale(): string {
        const lang = String(appStore.get("language") || "").toLowerCase();
        if (!lang) return undefined as unknown as string;
        if (lang === "eslat") return "es";
        return lang;
    }

    // ── formatting bound to the user's locale ──
    formatCount = (n: number) => fmt.formatCount(n, this.uiLocale);
    formatPercent = (value: number, digits = 1) =>
        fmt.formatPercent(value, this.uiLocale, digits);
    formatShortDay = (iso: string) => fmt.formatShortDay(iso, this.uiLocale);
    formatDateTime = fmt.formatDateTime;

    relativeTime = (ts: number): string => {
        const diff = Math.max(0, Date.now() - ts);
        const mins = Math.round(diff / 60_000);
        const rtf =
            typeof Intl !== "undefined" &&
            "RelativeTimeFormat" in Intl &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            new (Intl as any).RelativeTimeFormat(this.uiLocale, {
                numeric: "always",
            });

        if (mins < 60) {
            return rtf
                ? rtf.format(-mins, "minute")
                : `${translate("TIME_AGO", "hace")} ${mins} ${translate(
                      "TIME_MINUTES_SHORT",
                      "min",
                  )}`;
        }
        const hours = Math.round(mins / 60);
        if (hours < 24) {
            return rtf
                ? rtf.format(-hours, "hour")
                : `${translate("TIME_AGO", "hace")} ${hours} ${translate(
                      "TIME_HOURS_SHORT",
                      "h",
                  )}`;
        }
        const days = Math.round(hours / 24);
        return rtf
            ? rtf.format(-days, "day")
            : `${translate("TIME_AGO", "hace")} ${days} ${translate(
                  "TIME_DAYS_SHORT",
                  "d",
              )}`;
    };

    formatStatus = (status: string): string => {
        if (status === "active") return translate("STATUS_ACTIVE", "Activa");
        if (status === "trialing")
            return translate("STATUS_TRIAL", "En prueba");
        if (status === "past_due") return translate("STATUS_UNPAID", "Impago");
        if (status === "canceled")
            return translate("STATUS_CANCELED", "Cancelada");
        if (status === "installed")
            return translate("STATUS_INSTALLED", "Instalación");
        if (status === "registered")
            return translate("STATUS_REGISTERED", "Registro");
        if (status === "subscribed")
            return translate("STATUS_SUBSCRIBED", "Suscriptor");
        return status;
    };

    setSection = (id: SectionId) => {
        this.section = id;
        writeSectionToUrl(id);
        if (typeof window !== "undefined") window.scrollTo({ top: 0 });
    };

    // ── snackbar ──
    toast = (text: string) => {
        this.snackBarText = text;
        this.activeSnackBar = true;
        if (this.#snackTimer) clearTimeout(this.#snackTimer);
        this.#snackTimer = setTimeout(() => {
            this.activeSnackBar = false;
            this.#snackTimer = null;
        }, SNACKBAR_MS);
    };

    deactiveSnackbar = () => {
        this.activeSnackBar = false;
        if (this.#snackTimer) {
            clearTimeout(this.#snackTimer);
            this.#snackTimer = null;
        }
    };

    /** Call from the component's cleanup. */
    dispose = () => {
        if (this.#snackTimer) clearTimeout(this.#snackTimer);
        this.#snackTimer = null;
    };

    // ── referral code editing ──
    setReferralCode = (val: string) => {
        this.referralCode = (val || "").trim();
    };
    enableEditCode = () => {
        this.editingCode = true;
    };
    cancelEditCode = () => {
        this.referralCode = this.oldReferralCode;
        this.editingCode = false;
    };

    copyLink = () => {
        copy(this.referralLink);
        this.toast(translate("PARTNER_LINK_COPIED", "Enlace copiado"));
    };

    downloadQR = () => downloadQr(this.referralLink, this.referralCode);

    // ── lifecycle ──
    /** Same guards and order as the previous onMount body. */
    init = async () => {
        console.log("Referrals App mounted");
        this.loading = true;

        // Prune old metrics caches early (best-effort)
        try {
            appStore.cleanup((k, v) => {
                if (typeof k !== "string") return false;
                if (!k.startsWith("partnerMetricsCache:v1:")) return false;
                if (!isRecord(v)) return true;
                const fetchedAt = asNumber((v as any).fetchedAt, 0);
                if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return true;
                return Date.now() - fetchedAt > METRICS_CACHE_MAX_AGE_MS;
            });
        } catch {
            // ignore
        }

        // 1) Cargar usuario (mismo origen que la otra plataforma)
        this.user = appStore.get("user") || null;
        if (!this.user) {
            this.loading = false;
            goTo("/logout");
            return;
        }

        // Partner onboarding: enforce steps 1-3 at least once
        try {
            const id = String(this.user?.id || "").trim();
            const currentCode = String(this.user?.referralCode || "").trim();
            const currentName = String(this.user?.name || "").trim();
            if (!currentName || !currentCode || (id && currentCode === id)) {
                this.loading = false;
                goTo("/partnerOnboarding");
                return;
            }
        } catch {
            // ignore
        }

        this.section = readSectionFromUrl();

        // 2) Inicializar código y enlace (si no hay referralCode, usa user.id)
        this.referralCode = (
            this.user?.referralCode ||
            this.user?.id ||
            ""
        ).trim();
        this.oldReferralCode = this.referralCode;

        // 3) Comprobar Stripe
        if (this.user?.id && this.user?.stripeAccountId) {
            try {
                const response = await checkStripeAccountSE(
                    this.user.id,
                    this.user.stripeAccountId,
                );
                if (response?.accountId) {
                    this.stripeAccountId = response.accountId;
                    this.stripeReady = !!response.ready;

                    // Persistimos en local
                    const updatedUser = {
                        ...this.user,
                        stripeAccountId: response.accountId,
                    };
                    this.user = updatedUser;
                    appStore.set("user", updatedUser);

                    this.toast(
                        this.stripeReady
                            ? LANG.LANG_STRIPE_CONNECTED
                            : LANG.LANG_STRIPE_PENDING,
                    );
                } else {
                    this.stripeAccountId = null;
                    this.stripeReady = false;
                }
            } catch (err) {
                console.error("❌ Error checking Stripe account:", err);
            }
        } else {
            this.stripeAccountId = null;
            this.stripeReady = false;
        }

        // 4) Revisar return_url/refresh_url de Stripe
        const url = window.location.href;
        if (url.includes("connected")) {
            this.toast(LANG.LANG_STRIPE_CONNECTED);
        } else if (url.includes("refresh")) {
            this.toast(LANG.LANG_STRIPE_LINK_EXPIRED);
        }
        // One-shot params: don't re-toast on reload.
        stripUrlParams(["connected", "refresh"]);

        // 5) Cargar métricas iniciales
        await this.loadMetrics();

        this.loading = false;
    };

    // ── metrics ──
    #applyMetrics(res: any) {
        const m = parseMetrics(res);
        this.installs = m.installs;
        this.signups = m.signups;
        this.subscribers = m.subscribers;
        this.series = m.series;
        this.activity = m.activity;
        this.subscriptionsList = m.subscriptionsList;
        this.usersList = m.usersList;
        this.metricsHasSeries = m.series.length > 0;
        this.metricsHasActivity = m.activity.length > 0;
        this.metricsHasSubscriptions = m.subscriptionsList.length > 0;
        this.metricsHasUsers = m.usersList.length > 0;
    }

    async #warmMetricsCaches(userId: string, referralId: string) {
        if (this.#warmingMetricsCache) return;
        this.#warmingMetricsCache = true;

        try {
            const allRanges: RangeKey[] = ["7d", "30d", "90d", "all"];
            for (const rk of allRanges) {
                const cached = readMetricsCache(userId, referralId, rk);
                if (cached && isCacheFresh(cached)) continue;

                try {
                    const res = await getReferralMetricsCachedSE(
                        userId,
                        referralId,
                        buildMetricsQuery(rk),
                    );
                    writeMetricsCache(userId, referralId, rk, {
                        fetchedAt: Date.now(),
                        res,
                    });
                } catch {
                    // ignore warm errors
                }
            }
        } finally {
            this.#warmingMetricsCache = false;
        }
    }

    loadMetrics = async () => {
        const referralId = (
            this.referralCode ||
            this.user?.referralCode ||
            ""
        ).trim();
        if (!this.user?.id || !referralId) return;

        const userId = String(this.user.id);
        const range = this.range;
        let cached: MetricsCacheEntry | null = readMetricsCache(
            userId,
            referralId,
            range,
        );

        if (cached && isCacheFresh(cached)) {
            this.lastMetricsUpdatedAt = cached.fetchedAt;
            this.#applyMetrics(cached.res);
            void this.#warmMetricsCaches(userId, referralId);
            return;
        }

        try {
            const res = await getReferralMetricsCachedSE(
                userId,
                referralId,
                buildMetricsQuery(range),
            );
            console.log("✅ metrics loaded (cached or fresh):", res);

            const fetchedAt = Date.now();
            writeMetricsCache(userId, referralId, range, { fetchedAt, res });
            this.lastMetricsUpdatedAt = fetchedAt;
            this.#applyMetrics(res);
            void this.#warmMetricsCaches(userId, referralId);
        } catch (err) {
            // Fallback: keep showing last cached data (even if stale)
            cached = cached || readMetricsCache(userId, referralId, range);
            if (cached) {
                this.lastMetricsUpdatedAt = cached.fetchedAt;
                this.#applyMetrics(cached.res);
            }

            console.error("❌ metrics error:", err);
        }
    };

    // ── persist user (mantiene compat) ──
    updateUser = async () => {
        try {
            this.loading = true;
            // Sync local
            const user = appStore.get("user") || {};
            user.stripeAccountId = this.stripeAccountId;
            user.referralCode = this.referralCode;
            appStore.set("user", user);
            this.user = user;

            // Sync Firebase
            await updateDocById("users", user.firebaseUser, { ...user });

            this.toast(LANG.LANG_PROFILE_UPDATED);
        } catch (e) {
            console.error("❌ updateUser error:", e);
        } finally {
            this.loading = false;
        }
    };

    // ── stripe connect ──
    createStripeAccount = async () => {
        try {
            this.loading = true;
            // Usamos referralCode como "referralId" (igual que Vue)
            const res = await createStripeAccountSE(
                this.user.id,
                this.referralCode,
            );
            this.stripeAccountId = res.accountId;
            this.toast(LANG.LANG_STRIPE_CREATED);
            await this.updateUser();
        } catch (err: any) {
            console.error("❌ createStripeAccount error:", err);
            if (err?.response?.status === 409) {
                this.toast(LANG.LANG_ERROR_CODE_EXISTS);
            }
        } finally {
            this.loading = false;
        }
    };

    createAccountLink = async () => {
        try {
            if (!this.stripeAccountId) return;
            const res = await createStripeAccountLinkSE(
                this.user.id,
                this.stripeAccountId,
            );
            window.open(res.url, "_blank");
        } catch (err) {
            console.error("❌ createAccountLink error:", err);
        }
    };

    goStripePanel = async () => {
        try {
            if (!this.stripeAccountId) return;
            const res = await createStripeDashboardPortalSE(
                this.user.id,
                this.stripeAccountId,
            );
            window.open(res.url, "_blank");
        } catch (err) {
            console.error("❌ goStripePanel error:", err);
        }
    };

    checkStripeStatus = async () => {
        try {
            if (!this.stripeAccountId) return;
            const res = await checkStripeAccountSE(
                this.user.id,
                this.stripeAccountId,
            );
            this.stripeReady = !!res.ready;
            this.toast(
                this.stripeReady
                    ? LANG.LANG_STRIPE_READY_SNACK
                    : LANG.LANG_STRIPE_PENDING_SNACK,
            );
        } catch (err) {
            console.error("❌ checkStripeStatus error:", err);
        }
    };

    // ── referral code (update en backend) ──
    updateReferralCode = async () => {
        try {
            this.loading = true;
            await updateReferralCodeSE(this.user.id, this.referralCode);
            this.oldReferralCode = this.referralCode;
            this.toast(LANG.LANG_SUCCESS_UPDATE_CODE);
            await this.updateUser();
            await this.loadMetrics();
            this.editingCode = false;
        } catch (err) {
            console.error("❌ updateReferralCode error:", err);
            this.toast(LANG.LANG_ERROR_CODE_EXISTS);
        } finally {
            this.loading = false;
        }
    };
}

const CTX = Symbol("partner-dashboard");

export function setDashboardCtx(store: DashboardStore) {
    setContext(CTX, store);
    return store;
}

export function getDashboardCtx(): DashboardStore {
    return getContext<DashboardStore>(CTX);
}
